import type { Options } from 'ky';
import type { Address } from 'viem';

// eslint-disable-next-line deprecate/import
import { type WagmiConfigChain } from '@shared/common/providers/LegacyWeb3Provider/wagmi';
import type { JsonApiObject } from '@shared/helpers/jsonapi';
import { deserializeJsonApi } from '@shared/helpers/jsonapi';

import type {
  DeserializedSelfAllocations,
  LaunchpadSalePoolStatus,
  LaunchpadSalePoolType,
  LaunchpadSalePoolWhitelistStrategy,
} from '@api/types';

import type { ApiOptions } from './ApiProvider';
import Api from './ApiProvider';
import { subscribeToMagicIdNewsletter } from './newsletter';
import type { PaginationSearchParams } from './types/common';

export type Banner = {
  id: string;
  mobileImage: string;
  desktopImage: string;
  altText: string;
  link: string;
  position: number;
  group: 'MAIN_PAGE';
  isHidden: boolean;
};

export type Campaign = {
  id: string;
  bannerImage: string;
  logoImage: string;
  title: string;
  status: string;
  link: string;
  prizePool: number;
  altText: string;
  buttonLabel: string;
  guaranteedAllocation: number;
  totalTokens: number;
  totalPrice: number;
  group: 'MAIN_PAGE';
  isHidden: boolean;
};

export interface GetKYCAccessTokenResponse {
  token: string;
}

export const getKYCAccessToken = (
  options: ApiOptions<{
    json: {
      sourceId?: string;
    };
  }>,
) => {
  return Api.post('v2/magicid/user/kyc/token', options).json<GetKYCAccessTokenResponse>();
};

export interface GetKYCBabt {
  hasBABT: boolean;
}

export const getKYCBabt = () => {
  return Api.post('v2/magicid/user/kyc/babt').json<GetKYCBabt>();
};

export const subscribeToLaunchpadNewsletter = (
  options: ApiOptions<{
    json: {
      email: string;
    };
  }>,
) => {
  return subscribeToMagicIdNewsletter({
    ...options,
    json: {
      ...options.json,
      group: 'LAUNCHPAD',
    },
  });
};

export const PORTFOLIO_POOL_PROJECT_CLAIMING_TYPE = {
  INTERNAL: 'INTERNAL',
  EXTERNAL: 'EXTERNAL',
} as const;

type PortfolioPoolProjectClaimingTypeName = keyof typeof PORTFOLIO_POOL_PROJECT_CLAIMING_TYPE;
type PortfolioPoolProjectClaimingType =
  (typeof PORTFOLIO_POOL_PROJECT_CLAIMING_TYPE)[PortfolioPoolProjectClaimingTypeName];

interface PortfolioPoolProject {
  name: string;
  title: string;
  meta: string;
  description: string;
  logoUrl: string;
  ticker: string;
  tags: string[];
  startDate: string;
  endDate: string;
  claimingStartDate: string;
  estimatedListingDate: string;
  tokenSupply: number;
  projectValuation: number;
  archived: boolean;
  isComingSoon: boolean;
  claimingBlockchain: string;
  claimingBlockchainId: WagmiConfigChain['id'];
  claimingType: PortfolioPoolProjectClaimingType;
  claimingInfo: any;
  bannerUrls: any;
  bannerSmallUrl: string;
  bannerUrl: string;
  links: { link: string; type: string; title: string }[];
  slides: any;
  highlights: { title: string; description: string }[];
  vestings: {
    tba: boolean;
    startTitle: string;
    startPercent: number;
    vestingPercent: number;
    startDescription: string;
    vestingStartDate: string;
    vestingDescription: string;
  };
  slug: string;
  restrictedCountries: string[];
  id: string;
}

export interface DeserializedSelfPool {
  id: string;
  price: number;
  name: string;
  type: LaunchpadSalePoolType;
  goal: number;
  paymentsReceived: number;
  announcement: string | null;
  participantCount: number;
  minAllocation: number;
  maxAllocation: number;
  startDate: string;
  endDate: string;
  externalLink: string | null;
  depositContractAddress: Address;
  depositTokenPrice: number;
  depositTokenTicker: string;
  claimingStartDate: string;
  claimingEndDate: string | null;
  claimContractAddress: Address | null;
  fullRefundAvailable: boolean;
  fullRefundProcessingEndDate: string; // ISO date string
  fullRefundProcessingFee: number;
  fullRefundProcessingStartDate: string; // ISO date string
  status: LaunchpadSalePoolStatus;
  meta: {
    whiteListExists: boolean;
    whiteListContainsUser: boolean;
  };
  /**
   * some times it can be null because of misconfiguration in cms
   * we need to handle this case to prevent breaking ui
   */
  project: PortfolioPoolProject | null;
  whiteList: {
    id: string;
    criteriaCheckStrategy: LaunchpadSalePoolWhitelistStrategy;
    allocationSize: number;
    startDate: string;
    endDate: string;
  } | null;
  refundDate: string | null;
  refund?: boolean;
}

export interface DeserializedStandaloneVesting {
  id: string;
  name?: string;
  slug: string;
  description?: string;
  blockchainNetwork: string;
  blockchainNetworkId: number;
  tokenAddress: Address;
  tokenTicker: string;
  cliffPeriod: number;
  firstUnlockPercent: number;
  unlockPeriod: number;
  unlockPeriodPercent: number;
  startDate: string;
  endDate: string;
  status: 'ACTIVE';
  allocationsUploadedPercent: number;
  totalAllocations: number;
  address: Address;
  totalAmount: number;
}

export interface DeserializedSelfWhitelistPoolJoiningCriteriaResult {
  verified: boolean;
  whiteListJoiningCriteria: {
    id: string;
    title: string;
    description: string;
    settings: Record<string, string>;
  };
}

// NOTE: max 20 or back will return error
// NOTE: need 5, but made 6 to load 1 in advance to show see more button correctly
export const POOLS_DEFAULT_PAGE_LIMIT = 6;

export const getSelfActiveWhitelistingPools = async (
  options?: ApiOptions<{ searchParams?: PaginationSearchParams }>,
): Promise<DeserializedSelfPool[]> => {
  const optionsWithDefaults: typeof options = {
    ...options,
    searchParams: {
      ...options?.searchParams,
      limit: POOLS_DEFAULT_PAGE_LIMIT,
    },
  };

  const data = await Api.get(
    'launchpad/users/me/active-whitelisting-pools',
    optionsWithDefaults,
  ).json<any>();

  return deserializeJsonApi<DeserializedSelfPool[]>(data);
};

export const getSelfActiveSalesPools = async (
  options?: ApiOptions<{ searchParams?: PaginationSearchParams }>,
): Promise<DeserializedSelfPool[]> => {
  const optionsWithDefaults: typeof options = {
    ...options,
    searchParams: {
      ...options?.searchParams,
      limit: POOLS_DEFAULT_PAGE_LIMIT,
    },
  };

  const data = await Api.get(
    'launchpad/users/me/active-sales-pools',
    optionsWithDefaults,
  ).json<any>();

  const deserialized = await deserializeJsonApi<DeserializedSelfPool[]>(data);

  return deserialized;
};

export const getSelfAllocations = async (
  options?: Options,
): Promise<DeserializedSelfAllocations> => {
  const data = await Api.get('launchpad/users/me/allocations', options).json();

  const deserialized = await deserializeJsonApi<DeserializedSelfAllocations>(data);

  return deserialized;
};

export const getSelfClaimablePools = async (
  options?: ApiOptions<{ searchParams?: PaginationSearchParams }>,
): Promise<DeserializedSelfPool[]> => {
  const optionsWithDefaults: typeof options = {
    ...options,
    searchParams: {
      ...options?.searchParams,
      limit: POOLS_DEFAULT_PAGE_LIMIT,
    },
  };

  const data = await Api.get('launchpad/users/me/claimable-pools', optionsWithDefaults).json<any>();

  const deserialized = await deserializeJsonApi<DeserializedSelfPool[]>(data);

  return deserialized;
};

export const getSelfStandaloneVestings = async (
  options?: ApiOptions<{ searchParams?: PaginationSearchParams }>,
): Promise<DeserializedStandaloneVesting[]> => {
  const optionsWithDefaults: typeof options = {
    ...options,
    searchParams: {
      ...options?.searchParams,
      limit: POOLS_DEFAULT_PAGE_LIMIT,
    },
  };

  const data = await Api.get(
    'launchpad/users/me/claimable-standalone-vestings',
    optionsWithDefaults,
  ).json<any>();
  const deserialized = await deserializeJsonApi<DeserializedStandaloneVesting[]>(data);

  return deserialized;
};

export const getSelfClosedPools = async (
  options?: ApiOptions<{ searchParams?: PaginationSearchParams }>,
): Promise<DeserializedSelfPool[]> => {
  const optionsWithDefaults: typeof options = {
    ...options,
    searchParams: {
      ...options?.searchParams,
      limit: POOLS_DEFAULT_PAGE_LIMIT,
    },
  };
  const data = await Api.get('launchpad/users/me/closed-pools', optionsWithDefaults).json<any>();

  const deserialized = await deserializeJsonApi<DeserializedSelfPool[]>(data);

  return deserialized;
};

export const getSelfWhitelistPoolJoiningCriteriaResults = async (
  poolId: string,
  options?: ApiOptions,
): Promise<{
  criteriaResults: DeserializedSelfWhitelistPoolJoiningCriteriaResult[];
  meta: {
    participationVerified: boolean;
  };
}> => {
  const data = await Api.get(
    `launchpad/pools/${poolId}/white-list/joining-criteria`,
    options,
  ).json<{ meta: { participationVerified: boolean } }>();

  const deserialized = await deserializeJsonApi<
    DeserializedSelfWhitelistPoolJoiningCriteriaResult[]
  >(data);

  return {
    criteriaResults: deserialized,
    meta: data.meta,
  };
};

export const verifySelfPoolWhitelist = async (
  poolId: string,
  options?: ApiOptions,
): Promise<{
  criteriaResults: DeserializedSelfWhitelistPoolJoiningCriteriaResult[];
  meta: {
    participationVerified: boolean;
  };
}> => {
  const data = await Api.post(`launchpad/pools/${poolId}/white-list/participants`, options).json<{
    meta: { participationVerified: boolean };
  }>();

  const deserialized = await deserializeJsonApi<
    DeserializedSelfWhitelistPoolJoiningCriteriaResult[]
  >(data);

  return {
    criteriaResults: deserialized,
    meta: data.meta,
  };
};

export const getBanners = async () => {
  const data = await Api.get('launchpad/banners').json<JsonApiObject>();

  return deserializeJsonApi<Banner[]>(data);
};

export const getCampaignsProjects = async () => {
  const data = await Api.get('launchpad/campaigns').json<JsonApiObject>();

  return deserializeJsonApi<Campaign[]>(data);
};

export interface PoolDepositFeeResponse {
  data: {
    id: string;
    type: string;
    attributes: {
      type: string;
      title: string;
      description: string;
    };
  };
  meta: {
    feeConfiguredValue: number;
    isFeeConfigInPercent: boolean;
    feeCalculatedValue: number;
  };
}

export interface PoolDepositFee {
  feeConfiguredValue: number;
  isFeeConfigInPercent: boolean;
  feeCalculatedValue: number;
}

interface PoolDepositFeeOptions {
  searchParams: {
    amount: number;
  };
}

export const getPoolDepositFee = async (
  poolId: string,
  options?: ApiOptions<PoolDepositFeeOptions>,
): Promise<PoolDepositFee> => {
  const response = await Api.get<PoolDepositFeeResponse>(
    `launchpad/pools/${poolId}/deposit-fee`,
    options,
  ).json();

  return {
    ...response.meta,
  };
};
